import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import DashboardLayout from "../components/DashboardLayout";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useI18n } from "../contexts/I18nContext";
import CountUp from "../components/CountUp";
import { Briefcase, FileText, Sparkles, CreditCard, ArrowUpRight, Zap, Lock } from "lucide-react";

function Stat({ label, value, Icon, testid, animate = true }) {
  return (
    <div className="card-premium p-6" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="text-[11px] tracking-[0.22em] uppercase font-bold text-[#8B1E3F]">{label}</div>
        <Icon className="h-4 w-4 text-slate-400" />
      </div>
      <div className="mt-3 font-display font-bold text-4xl text-slate-900 tabular-nums">
        {animate && typeof value === "number" ? (
          <CountUp value={value} duration={1100} />
        ) : (
          value
        )}
      </div>
    </div>
  );
}

function QuotaBar({ used, limit, label }) {
  const pct = limit ? Math.min(100, Math.round((used / limit) * 100)) : 0;
  const danger = pct >= 90;
  return (
    <div data-testid="quota-bar">
      <div className="flex items-center justify-between text-sm">
        <span className="text-slate-700 font-medium">{label}</span>
        <span className="font-mono text-slate-600">{used} / {limit}</span>
      </div>
      <div className="mt-2 h-2 w-full bg-slate-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all ${danger ? "bg-red-500" : "bg-[#0A192F]"}`}
             style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const [matters, setMatters] = useState([]);
  const [usage, setUsage] = useState(null);

  useEffect(() => {
    api.get("/matters").then((res) => setMatters(res.data)).catch(() => {});
    api.get("/usage/me").then((res) => setUsage(res.data)).catch(() => {});
  }, []);

  const reportsCount = matters.reduce((a, m) => a + (m.report_count || 0), 0);

  return (
    <DashboardLayout>
      <div className="max-w-6xl">
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div>
            <div className="kicker">{t("dash.welcome")}</div>
            <h1 className="mt-2 font-display font-bold text-3xl md:text-4xl text-slate-900 tracking-tight">
              {user?.full_name?.split(" ")[0] || user?.email}
            </h1>
            <p className="mt-2 text-slate-600">
              {lang === "el" ? "Ο πίνακας σας στο Dikigoros.io." : "Your Dikigoros.io dashboard."}
            </p>
          </div>
          <Link to="/new-analysis" className="btn-primary rounded-full px-5 py-3 text-sm font-semibold inline-flex items-center gap-2" data-testid="dash-new-analysis">
            <Sparkles className="h-4 w-4" /> {t("dash.newAnalysis")}
          </Link>
        </div>

        <div className="mt-10 grid md:grid-cols-3 gap-5">
          <Stat label={t("dash.matters")}  value={matters.length}  Icon={Briefcase} testid="stat-matters" />
          <Stat label={t("dash.analyses")} value={reportsCount}    Icon={FileText}  testid="stat-reports" />
          <div className="card-premium p-6" data-testid="stat-plan">
            <div className="flex items-center justify-between">
              <div className="text-[11px] tracking-[0.22em] uppercase font-bold text-[#8B1E3F]">{t("dash.plan")}</div>
              <CreditCard className="h-4 w-4 text-slate-400" />
            </div>
            <div className="mt-3 font-display font-bold text-3xl text-slate-900 capitalize">
              {usage?.plan || user?.plan || "Free"}
              {usage?.in_trial && (
                <span className="ml-2 text-[10px] uppercase tracking-wider font-bold text-[#8B1E3F] align-middle">
                  {lang === "el" ? "Trial" : "Trial"}
                </span>
              )}
            </div>
            <Link to="/pricing" className="mt-3 inline-flex items-center gap-1 text-sm text-[#0A192F] font-medium hover:underline">
              {t("dash.upgrade")} <ArrowUpRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>

        {usage && (
          <div className="mt-6 card-premium p-6" data-testid="usage-card">
            <div className="flex items-center gap-2">
              <Zap className="h-4 w-4 text-[#8B1E3F]" />
              <div className="kicker !text-slate-500">{lang === "el" ? "Μηνιαία χρήση" : "Monthly usage"}</div>
            </div>
            <div className="mt-5 space-y-4">
              <QuotaBar
                used={usage.ai_units_used}
                limit={usage.ai_units_limit}
                label={lang === "el" ? "Μονάδες AI / μήνα" : "AI Units / month"}
              />
              {usage.active_matters_limit && (
                <QuotaBar
                  used={usage.active_matters}
                  limit={usage.active_matters_limit}
                  label={lang === "el" ? "Ενεργές υποθέσεις" : "Active matters"}
                />
              )}
            </div>
            {usage.ai_units_used >= usage.ai_units_limit && (
              <div className="mt-5 rounded-lg bg-amber-50 border border-amber-200 p-4 text-sm text-amber-900 flex items-start gap-3" data-testid="quota-warning">
                <Lock className="h-4 w-4 mt-0.5 flex-none" />
                <div className="flex-1">
                  {lang === "el"
                    ? "Φτάσατε το μηνιαίο όριο. Αναβαθμίστε για να συνεχίσετε."
                    : "You've reached your monthly limit. Upgrade to continue."}
                  <Link to="/pricing" className="ml-2 underline font-semibold">{t("dash.upgrade")}</Link>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="mt-12">
          <div className="flex items-center justify-between">
            <h2 className="font-display font-bold text-2xl text-slate-900">{t("dash.recent")}</h2>
            <Link to="/matters" className="text-sm text-[#0A192F] hover:underline">{t("dash.matters")} →</Link>
          </div>
          <div className="mt-6">
            {matters.length === 0 ? (
              <div className="card-premium p-10 text-center" data-testid="dash-empty">
                <div className="text-slate-500 text-sm">{t("dash.noMatters")}</div>
                <Link to="/new-analysis" className="btn-primary inline-block mt-5 rounded-full px-5 py-2.5 text-sm font-semibold">
                  {t("dash.createFirst")}
                </Link>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-4">
                {matters.slice(0, 6).map((m) => (
                  <Link to={`/matters/${m.id}`} key={m.id} className="card-premium p-6 block" data-testid={`dash-matter-${m.id}`}>
                    <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-[#8B1E3F]">Matter</div>
                    <div className="mt-2 font-display font-bold text-lg text-slate-900">{m.title}</div>
                    <div className="mt-3 flex gap-4 text-xs text-slate-500">
                      <div>{m.document_count} docs</div>
                      <div>{m.report_count} reports</div>
                      <div>{new Date(m.updated_at).toLocaleDateString()}</div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
